import React, { useRef, useState } from "react";
import { useCanvasSize } from "../../FloorPlanModal/hooks/useCanvasSize";
import { isOnDuty } from "../hooks/useLiveSiteData";
import LiveCanvas from "./LiveCanvas";
import LayerControls, { useMapLayers } from "./LayerControls";
import OffSiteTitle from "./OffSiteTitle";

const ALL_SHOWN = Object.freeze({
  stations: true,
  symbols: true,
  equipment: true,
  tripwires: true,
});

/**
 * The live site canvas, shared by the live dashboard and the 即時定位 widget
 * so the two cannot drift: the map/plan layers, the 顯示 switches (基站,
 * 物件, 機具, 越線), LiveCanvas with every prop, the 圖層 control, and the
 * 場外 title status with its fit. Hosts differ only in the frame around it.
 *
 * @param {object}   data      useLiveSiteData() result
 * @param {object[]} [personnel] the people to draw (default: those on duty)
 * @param {Function} frame     (withStatus, body) => the host's frame around
 *                             `body`; withStatus(title) adds the 場外 status
 * @param {object}   [containerStyle] extra style for the measured box
 */
const LiveSiteCanvas = ({
  machineID,
  data,
  personnel,
  selectedPersonnelId,
  onPersonnelClick,
  onViewTransformChange = null,
  containerStyle,
  frame,
}) => {
  const canvasContainerRef = useRef(null);
  const canvasRef = useRef(null);
  // 顯示 is view-only: 越線 off draws no lines, alerts still come in.
  const [shown, setShown] = useState(ALL_SHOWN);
  const [fitOffSiteKey, setFitOffSiteKey] = useState(0);

  const { floorPlan, placedSymbols, baseStations, equipment, tripwires } = data;
  const { dimensions, layoutType, imageUrl, loading } = floorPlan;
  const layers = useMapLayers({ siteId: machineID, ...floorPlan });
  // fillContainer: live-only. Drops the editor's axis-label gutters and lets
  // the canvas fill its box, with the floor fitted inside by the viewport.
  const canvasSize = useCanvasSize(
    dimensions,
    true,
    canvasContainerRef,
    layoutType,
    { fillContainer: true },
  );
  const people = personnel ?? data.personnel.filter(isOnDuty);

  const row = (label, key, count) => ({
    label,
    count,
    visible: shown[key],
    onToggle: () => setShown((s) => ({ ...s, [key]: !s[key] })),
  });
  const toggles = [
    row("基站", "stations", baseStations.length),
    row("物件", "symbols", placedSymbols.length),
    row("機具", "equipment", equipment.length),
    row("越線", "tripwires", tripwires.length),
  ];

  const withStatus = (title) => (
    <OffSiteTitle
      title={title}
      personnel={people}
      equipment={equipment}
      onFit={
        layoutType === "plane"
          ? () => setFitOffSiteKey((k) => k + 1)
          : undefined
      }
    />
  );

  const body = (
    <div
      ref={canvasContainerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        ...containerStyle,
      }}
    >
      <LiveCanvas
        canvasRef={canvasRef}
        machineID={machineID}
        dimensions={dimensions}
        canvasSize={canvasSize}
        layoutType={layoutType}
        imageUrl={imageUrl}
        personnel={people}
        baseStations={baseStations}
        equipment={equipment}
        equipmentVisible={shown.equipment}
        tripwires={shown.tripwires ? tripwires : []}
        placedSymbols={placedSymbols}
        symbolsVisible={shown.symbols}
        stationsVisible={shown.stations}
        selectedPersonnelId={selectedPersonnelId}
        onPersonnelClick={onPersonnelClick}
        onViewTransformChange={onViewTransformChange}
        loading={loading}
        layers={layers}
        fitOffSiteKey={fitOffSiteKey}
        layerControl={<LayerControls toggles={toggles} layers={layers} />}
      />
    </div>
  );

  return frame(withStatus, body);
};

export default LiveSiteCanvas;
